/**
 * Auth token storage. Access token lives in memory (XSS surface area kept
 * small) and the refresh token is set as an httpOnly cookie by the backend
 * so the browser side does not have to manage it.
 *
 * Bootstrap: on every full page load the app should call
 * {@link bootstrapSession} which silently exchanges the refresh cookie for a
 * fresh access token (or resolves to `null` if the user is signed out).
 */

let accessToken: string | null = null;
let refreshing: Promise<string | null> | null = null;
let bootstrapping: Promise<string | null> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Refresh the access token using the httpOnly refresh cookie. Concurrent
 * callers share a single in-flight request via a memoised promise.
 */
export async function refreshAccessToken(baseURL: string): Promise<string | null> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    try {
      const res = await fetch(`${baseURL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return null;
      const body = (await res.json()) as { accessToken?: string };
      const next = body.accessToken ?? null;
      setAccessToken(next);
      return next;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/**
 * Attempt to restore an existing session on app boot. Safe to call multiple
 * times — the in-flight result is memoised. Returns the new access token or
 * `null` if no valid session exists.
 *
 * Apps should `await bootstrapSession(baseURL)` before mounting their router
 * so that protected routes don't bounce authenticated users to /login on a
 * full page reload.
 */
export function bootstrapSession(baseURL: string): Promise<string | null> {
  if (accessToken) return Promise.resolve(accessToken);
  if (!bootstrapping) {
    bootstrapping = refreshAccessToken(baseURL).finally(() => {
      bootstrapping = null;
    });
  }
  return bootstrapping;
}
